/**
 * Persists hazard list filters in sessionStorage per Machinery Directive
 * compliance record so filters survive in-tab navigation away from the list.
 */

import {
  type HazardAdequacyFilter,
  type HazardListFilters,
  type HazardSourceFilter,
} from './hazardListFilters'

const STORAGE_KEY_PREFIX = 'machineryDirective.hazards.listFilters:' as const

const ADEQUACY_VALUES = new Set<HazardAdequacyFilter>([
  'all',
  'not_adequately_reduced',
  'adequately_reduced',
])

const SOURCE_VALUES = new Set<HazardSourceFilter>(['all', 'USER', 'AGENT'])

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') {
      return null
    }
    return window.sessionStorage
  } catch {
    return null
  }
}

function makeKey(machineryDirectiveComplianceId: string): string {
  return `${STORAGE_KEY_PREFIX}${machineryDirectiveComplianceId}`
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  )
}

function parseHazardListFilters(raw: string): HazardListFilters | null {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object') {
      return null
    }

    const record = parsed as Record<string, unknown>
    const adequacy = record.adequacy
    const category = record.category
    const location = record.location
    const ehsrClauseIds = record.ehsrClauseIds
    const includeNoEhsr = record.includeNoEhsr
    const lifecycles = record.lifecycles
    const source = record.source

    if (
      typeof adequacy !== 'string' ||
      !ADEQUACY_VALUES.has(adequacy as HazardAdequacyFilter)
    ) {
      return null
    }

    if (typeof category !== 'string') {
      return null
    }

    if (typeof location !== 'string') {
      return null
    }

    if (!isStringArray(ehsrClauseIds)) {
      return null
    }

    if (typeof includeNoEhsr !== 'boolean') {
      return null
    }

    if (!isStringArray(lifecycles)) {
      return null
    }

    if (
      typeof source !== 'string' ||
      !SOURCE_VALUES.has(source as HazardSourceFilter)
    ) {
      return null
    }

    return {
      adequacy: adequacy as HazardAdequacyFilter,
      category,
      location,
      ehsrClauseIds,
      includeNoEhsr,
      lifecycles,
      source: source as HazardSourceFilter,
    }
  } catch {
    return null
  }
}

export function saveHazardListFilters(
  machineryDirectiveComplianceId: string,
  filters: HazardListFilters
): void {
  const storage = getStorage()
  if (storage === null || machineryDirectiveComplianceId.length === 0) {
    return
  }

  try {
    storage.setItem(
      makeKey(machineryDirectiveComplianceId),
      JSON.stringify(filters)
    )
  } catch {
    // Quota, private mode, or disabled storage — ignore.
  }
}

export function readHazardListFilters(
  machineryDirectiveComplianceId: string
): HazardListFilters | null {
  const storage = getStorage()
  if (storage === null || machineryDirectiveComplianceId.length === 0) {
    return null
  }

  try {
    const raw = storage.getItem(makeKey(machineryDirectiveComplianceId))
    if (raw === null) {
      return null
    }
    return parseHazardListFilters(raw)
  } catch {
    return null
  }
}

/** Test-only helper to read the raw storage key for a compliance record. */
export function __getHazardListFiltersStorageKeyForTests(
  machineryDirectiveComplianceId: string
): string {
  return makeKey(machineryDirectiveComplianceId)
}
